/**
 * FlagshipStatesRegistry.ts
 * Lightweight master configuration for the 5 flagship US states (NY, CA, TX, FL, WA).
 * Decoupled from heavy per-state county polygon/lore JSON barrels for instant mobile startup.
 */

export type StateId = 'NY' | 'CA' | 'TX' | 'FL' | 'WA';

export interface StateSouvenirInfo {
  id: string;
  name: string;
  description: string;
  buff: {
    type: string;
    value: number;
    description: string;
  };
}

export interface StateData {
  id: StateId;
  name: string;
  nickname: string;
  isStarter: boolean;
  pinX: number;
  pinY: number;
  description: string;
  iconicLandmark: string;
  souvenirId: string;
  souvenirName: string;
  souvenirBuff: {
    type: string;
    value: number;
  };
  souvenir: StateSouvenirInfo;
  foodIds: string[];
  careerTrackIds: string[];
}

export const FLAGSHIP_STATES: Record<StateId, StateData> = {
  NY: {
    id: 'NY',
    name: 'New York',
    nickname: 'The Empire State',
    isStarter: true,
    pinX: 310,
    pinY: 160,
    description: 'Kinh đô tài chính Wall Street, sân khấu nhạc kịch Broadway và nhịp sống hối hả không ngủ.',
    iconicLandmark: 'Tượng Nữ Thần Tự Do (Statue of Liberty)',
    souvenirId: 'souvenir_ny_statue_liberty',
    souvenirName: 'Statue of Liberty Mini Figurine',
    souvenirBuff: { type: 'HAPPINESS_PASSIVE', value: 2.0 },
    souvenir: {
      id: 'souvenir_ny_statue_liberty',
      name: 'Statue of Liberty Mini Figurine',
      description: 'Mô hình Tượng Nữ Thần Tự Do tinh xảo đặt trên bàn làm việc.',
      buff: {
        type: 'HAPPINESS_PASSIVE',
        value: 2.0,
        description: '+2% Tinh thần thụ động mỗi phút'
      }
    },
    foodIds: ['food_ny_slice', 'food_ny_bagel', 'food_ny_cheesecake'],
    careerTrackIds: ['ny_wall_street', 'ny_broadway']
  },
  CA: {
    id: 'CA',
    name: 'California',
    nickname: 'The Golden State',
    isStarter: false,
    pinX: 60,
    pinY: 240,
    description: 'Thung lũng Silicon khởi nghiệp công nghệ, bờ biển Thái Bình Dương ngập nắng và lối sống phóng khoáng.',
    iconicLandmark: 'Cầu Cổng Vàng (Golden Gate Bridge)',
    souvenirId: 'souvenir_ca_golden_gate',
    souvenirName: 'Golden Gate Bridge Miniature',
    souvenirBuff: { type: 'ENERGY_PASSIVE', value: 3.0 },
    souvenir: {
      id: 'souvenir_ca_golden_gate',
      name: 'Golden Gate Bridge Miniature',
      description: 'Mô hình Cầu Cổng Vàng màu đỏ cam biểu tượng cho bờ Tây nước Mỹ.',
      buff: {
        type: 'ENERGY_PASSIVE',
        value: 3.0,
        description: '+3% Hồi phục Năng lượng khi nghỉ ngơi'
      }
    },
    foodIds: ['food_ca_double_burger', 'food_ca_avocado_toast', 'food_ca_napa_smoothie'],
    careerTrackIds: ['ca_ai_engineer', 'ca_indie_gamedev']
  },
  TX: {
    id: 'TX',
    name: 'Texas',
    nickname: 'The Lone Star State',
    isStarter: false,
    pinX: 180,
    pinY: 340,
    description: 'Đế chế năng lượng dầu khí & điện gió, thịt nướng hun khói gỗ sồi trứ danh và tinh thần khai phá kiên cường.',
    iconicLandmark: 'Pháo Đài Alamo & Lò BBQ Gỗ Sồi',
    souvenirId: 'souvenir_tx_longhorn',
    souvenirName: 'Vintage Longhorn & Stetson Hat',
    souvenirBuff: { type: 'WORK_PAY_PASSIVE', value: 2.0 },
    souvenir: {
      id: 'souvenir_tx_longhorn',
      name: 'Vintage Longhorn & Stetson Hat',
      description: 'Sừng bò Longhorn cổ điển kèm mũ cao bồi Stetson treo tường.',
      buff: {
        type: 'WORK_PAY_PASSIVE',
        value: 2.0,
        description: '+2% Tiền thưởng thù lao trên mọi ca làm việc'
      }
    },
    foodIds: ['food_tx_brisket', 'food_tx_tacos', 'food_tx_pecan_pie'],
    careerTrackIds: ['tx_energy_tycoon', 'tx_pitmaster']
  },
  FL: {
    id: 'FL',
    name: 'Florida',
    nickname: 'The Sunshine State',
    isStarter: false,
    pinX: 290,
    pinY: 380,
    description: 'Cảng hàng không vũ trụ Cape Canaveral, chuỗi nghỉ dưỡng biển nhiệt đới và nhịp điệu Calle Ocho rực rỡ.',
    iconicLandmark: 'Trung Tâm Vũ Trụ Kennedy & Đảo Key West',
    souvenirId: 'souvenir_fl_rocket_flamingo',
    souvenirName: 'Space Shuttle & Flamingo Diorama',
    souvenirBuff: { type: 'HAPPINESS_PASSIVE', value: 3.0 },
    souvenir: {
      id: 'souvenir_fl_rocket_flamingo',
      name: 'Space Shuttle & Flamingo Diorama',
      description: 'Sa bàn kết hợp tàu con thoi không gian và chim hồng hạc biển nhiệt đới.',
      buff: {
        type: 'HAPPINESS_PASSIVE',
        value: 3.0,
        description: '+3% Tinh thần thụ động mỗi phút'
      }
    },
    foodIds: ['food_fl_cuban_sandwich', 'food_fl_key_lime_pie', 'food_fl_orange_juice'],
    careerTrackIds: ['fl_aerospace', 'fl_resort_manager']
  },
  WA: {
    id: 'WA',
    name: 'Washington',
    nickname: 'The Evergreen State',
    isStarter: false,
    pinX: 70,
    pinY: 100,
    description: 'Cái nôi của điện toán đám mây toàn cầu, văn hóa cà phê đặc sản thủ công và những đồi thông xanh ngát.',
    iconicLandmark: 'Tháp Space Needle & Chợ Pike Place',
    souvenirId: 'souvenir_wa_space_needle',
    souvenirName: 'Space Needle Desktop Model',
    souvenirBuff: { type: 'STUDY_SPEED_PASSIVE', value: 5.0 },
    souvenir: {
      id: 'souvenir_wa_space_needle',
      name: 'Space Needle Desktop Model',
      description: 'Mô hình tháp Space Needle cao vút tạo cảm hứng tập trung học tập.',
      buff: {
        type: 'STUDY_SPEED_PASSIVE',
        value: 5.0,
        description: '+5% Tốc độ học chứng chỉ nghề'
      }
    },
    foodIds: ['food_wa_espresso', 'food_wa_cedar_salmon', 'food_wa_cherry_tart'],
    careerTrackIds: ['wa_cloud_architect', 'wa_specialty_roaster']
  }
};

export function getStateData(stateId: string): StateData | undefined {
  const upper = stateId.toUpperCase() as StateId;
  return FLAGSHIP_STATES[upper];
}

export function getAllStates(): StateData[] {
  return Object.values(FLAGSHIP_STATES);
}
